/* eslint-disable no-case-declarations */
import useProjectUpdateMutation from "@/api/mutation/useProjectUpdateMutation";
import useProjectQuery, { Program, Project } from "@/api/query/useProjectQuery";
import PercentageCircle from "@/components/shared/PercentageCircle";
import { Button } from "@/components/ui/button";
import UserAvatar from "@/components/ui/userAvatar";
import {
  Gantt,
  Task as GanntTask,
  ViewMode,
} from "@/components/views/custom/gantt/src";
import { darkenColor } from "@/components/views/custom/gantt/src/helpers/colorMixer";
import dateFormater from "@/helperFuntions/dateFormater";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import { useUser } from "@/hooks/useUser";
import { errorConfig } from "@/lib/toastError";
import { OrgListOfNonWorkingDaysEnum } from "@backend/src/schemas/organisationSchema";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import useProjectDuplicateMutation from "@/api/mutation/useDuplicateProject";
import { usePlanValidation } from "@/hooks/usePlan";
import TrashCan from "@/assets/svg/TrashCan.svg";
import Dialog from "@/components/common/Dialog";
import useRemoveProjectMutation from "@/api/mutation/useProjectRemoveMutation";
import TaskSubTaskForm from "@/components/tasks/taskSubTaskForm";
import { ScrollText, Settings } from "lucide-react";
import DuplicateIcon from "@/assets/svg/DuplicateIcon.svg";
import Users from "@/assets/svg/Users.svg";
import {
  PlanTypeEnumSchema,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import calculatePortfolioAndProgramSPIs from "@/helperFuntions/projectherarch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { columnsType } from "@/components/views/custom/gantt/src/types/public-types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useOrganisationDetailsQuery from "@/api/query/useOrganisationDetailsQuery";

export interface ProjectNode extends Project {
  type: "project";
  progress: number;
}

// A program in the final hierarchy
export interface ProgramNode {
  type: "program";
  programId: string;
  programName: string;
  progress: number;
  projects: ProjectNode[];
}

// A portfolio in the final hierarchy
export interface PortfolioNode {
  type: "portfolio";
  portfolioId: string;
  portfolioName: string;
  progress: number;
  projects: ProjectNode[]; // Projects directly under the portfolio
  programs: ProgramNode[];
}

// The final data structure for your state is a union of these types
export type HierarchyItem = PortfolioNode | ProgramNode | ProjectNode;
type Props = {
  projects: Project[];
  setFilterUnit: (data: number) => void;
  FilterUnit: number;
  expandAll: boolean;
  selectedDateType: "estimated" | "actual";
  compare: "estimatedToActual" | "actualToReCalculated" | "all" | "";
  setSelectedDateType: (data: "estimated" | "actual") => void;
  hierarchyData: HierarchyItem[];
  isOpenWorkLoad:boolean
};
type GanttTaskExtendedType = GanntTask & {
  subtask?: GanttTaskExtendedType[];
  duration: number;
  assign: any[];
  level: string;
  status: string;
  flag: string;
  baseLineTask: GanttTaskExtendedType[];
};
type expandedProject = Project & { type: string };
type ProjectHierarchy = {
  portfolioId?: string;
  portfolioName?: string;
  projects: expandedProject[];
  type: string;
  programs?: program[];
};
type program = {
  programId: string;
  programName?: string;
  type: string;
  projects: expandedProject[];
};
type Calculable<T> = T & {
  __totalDuration: number;
  __weightedProgress: number;
};
function GanttView({
  projects,
  FilterUnit,
  expandAll,
  setFilterUnit,
  compare,
  selectedDateType,
  isOpenWorkLoad,
}: Props) {
  const [expanded, setExpanded] = useState<string[]>();
  const [data, setData] = useState<Project[]>(projects);
  const removeProjectMutation = useRemoveProjectMutation();
  const { t } = useTranslation();
  const { user } = useUser();
  const [spiPortfolioAndProgram, setSpiPortfolioAndProgram] =
    useState<Record<string, number>>();
  const [OrganisationMemeber, setOrganisationMemeber] = useState<any[]>([]);
  const organisationDetailsQuery = useOrganisationDetailsQuery(
    localStorage.getItem("organisation-id") ?? ""
  );
    useEffect(() => {
    setOrganisationMemeber(
      organisationDetailsQuery.data?.data.data.userOrganisation ?? []
    );
  }, [organisationDetailsQuery.data?.data.data]);
  useEffect(() => {
    if (projects && projects.length) {
      setData(projects);
      setSpiPortfolioAndProgram(
        calculatePortfolioAndProgramSPIs(projects ?? [])
      );
    }
  }, [projects]);

  const navigate = useNavigate();
  const [zoomState, setZoomState] = useState(FilterUnit);
  const [view, setView] = useState(ViewMode.QuarterYear);
  const fallbackEndDate = (
    primary?: Date | string | null,
    fallback?: Date | string | null
  ): Date => {
    if (primary) return new Date(primary);
    if (fallback) return new Date(fallback);
    const date = new Date();
    date.setDate(date.getDate());
    return date;
  };
  function findLatestOverallEndDate(projects: Project[]): Date | undefined {
    let latest: Date | undefined;

    projects.forEach((project) => {
      // Estimated
      const estEnd = fallbackEndDate(
        project.estimatedEndDate,
        project.startDate
      );
      if (!latest || estEnd > latest) latest = estEnd;

      // Actual
      if (project.actualStartDate || project.actualEndDate) {
        const actStart = fallbackEndDate(
          project.actualStartDate,
          project.startDate
        );
        const actEnd = fallbackEndDate(project.actualEndDate, actStart);
        if (!latest || actEnd > latest) latest = actEnd;
      }

      // Recalculated
      if (
        project.reCalculateEndDate &&
        (project.actualStartDate || project.startDate)
      ) {
        const actStart = fallbackEndDate(
          project.actualStartDate,
          project.startDate
        );
        const recalcEnd = fallbackEndDate(project.reCalculateEndDate, actStart);
        if (!latest || recalcEnd > latest) latest = recalcEnd;
      }
    });
    return latest;
  }
  const convertProject = (projectsList: Project[]): GanttTaskExtendedType[] => {
    const hierarchy = buildCompleteHierarchy(projectsList);

    // --- PERFORMANCE OPTIMIZATION 1: Pre-calculate constants and create fast lookup Sets ---
    // These are calculated only ONCE, instead of inside every call to `calculateDuration`.
    const holidaySet = new Set(
      user?.userOrganisation[0].organisation.orgHolidays?.map((h: any) =>
        dateFormater(new Date(h.holidayStartDate))
      ) ?? []
    );
    const nonWorkingDaysSet = new Set(nonWorkingDays());

    // --- PERFORMANCE OPTIMIZATION 2: Memoization cache for duration calculation ---
    // Stores results of `calculateDuration` to avoid re-calculating for the same date range.
    const durationCache = new Map<string, number>();

    const isPortfolio = (item: any): item is ProjectHierarchy =>
      "programs" in item && "projects" in item && "portfolioId" in item;
    const isProgram = (item: any): item is Program =>
      "programId" in item && "programName" in item && !("startDate" in item);
    const isProject = (item: any): item is Project =>
      "projectId" in item && "startDate" in item;

    const ensureDatesValid = (
      startDate: Date,
      endDate: Date | null
    ): { start: Date; end: Date } => {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      let end = endDate ? new Date(endDate) : new Date(start);
      end.setHours(0, 0, 0, 0);

      if (start instanceof Date && !isNaN(start.getTime())) {
        if (end < start || !(end instanceof Date && !isNaN(end.getTime()))) {
          end = new Date(start);
        }
      } else {
        return {
          start: new Date("invalid date"),
          end: new Date("invalid date"),
        };
      }

      return { start, end };
    };

    // --- PERFORMANCE OPTIMIZATION 3: Use .some() for early exit ---
    const verification = (project: Project): boolean => {
      if (user?.userOrganisation[0]?.role === UserRoleEnumValue.ADMINISTRATOR) {
        return true;
      }
      // .some() is more efficient than .find() for boolean checks as it stops as soon as a match is found.
      return project.assignedUsers.some((item) => {
        return (
          (item.projectRole === UserRoleEnumValue.PROJECT_MANAGER ||
            item.projectRole === UserRoleEnumValue.ADMINISTRATOR) &&
          user?.userId === item.user.userId
        );
      });
    };

    // --- HEAVILY OPTIMIZED FUNCTION ---
    function calculateDuration(startDate: Date, endDate: Date): number {
      const start = new Date(startDate);
      const end = new Date(endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      if (
        !(start instanceof Date && !isNaN(start.getTime())) ||
        !(end instanceof Date && !isNaN(end.getTime()))
      ) {
        return 0;
      }

      // Use cache if this exact range has been calculated before
      const cacheKey = `${start.getTime()}-${end.getTime()}`;
      if (durationCache.has(cacheKey)) {
        return durationCache.get(cacheKey)!;
      }

      let duration = 0;
      const currentDate = new Date(start);

      // Loop from start date to end date
      while (currentDate <= end) {
        const day = currentDate.getDay();
        // Use the pre-calculated Sets for O(1) average time complexity lookup.
        // This is much faster than Array.prototype.includes() which is O(n).
        if (!nonWorkingDaysSet.has(day)) {
          const formattedDate = dateFormater(currentDate);
          if (!holidaySet.has(formattedDate)) {
            duration++;
          }
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }

      if (duration === 0 && start <= end) {
        // Before returning 1, ensure the single day is a working day
        const day = start.getDay();
        if (
          !nonWorkingDaysSet.has(day) &&
          !holidaySet.has(dateFormater(start))
        ) {
          durationCache.set(cacheKey, 1);
          return 1;
        }
      }

      // Cache the result before returning
      durationCache.set(cacheKey, duration);
      return duration;
    }

    const getSmartEndDateAndDuration = (
      item: Project,
      dateType: "actual" | "estimated"
    ): { actualStartDay: Date; actualLastDay: Date; duration: number } => {
      const start =
        dateType === "actual" && item.actualStartDate
          ? new Date(item.actualStartDate)
          : new Date(item.startDate);

      const potentialEnd = fallbackEndDate(
        dateType === "actual" ? item.actualEndDate : item.estimatedEndDate,
        start
      );

      const { start: actualStartDay, end: actualLastDay } = ensureDatesValid(
        start,
        potentialEnd
      );

      const duration =
        dateType === "actual"
          ? item.actualDuration ??
            item.estimatedDuration ??
            calculateDuration(actualStartDay, actualLastDay)
          : item.estimatedDuration ??
            item.actualDuration ??
            calculateDuration(actualStartDay, actualLastDay);

      return { actualStartDay, actualLastDay, duration };
    };

    // --- PERFORMANCE OPTIMIZATION 4: More efficient min/max date calculation ---
    const getMinMaxDatesFromTasks = (
      tasks: GanttTaskExtendedType[]
    ): { start?: Date; end?: Date } => {
      // Using timestamps avoids creating many new Date objects inside the loop.
      let minTime = Infinity;
      let maxTime = -Infinity;
      let validDatesFound = false;

      for (const t of tasks) {
        if (t.start) {
          const startTime = t.start.getTime();
          if (!isNaN(startTime)) {
            minTime = Math.min(minTime, startTime);
            validDatesFound = true;
          }
        }
        if (t.end) {
          // The 'end' property is the day *after* the last day.
          // Subtracting 1ms is a safe way to get the timestamp of the actual last day.
          const endTime = t.end.getTime() - 1;
          if (!isNaN(endTime)) {
            maxTime = Math.max(maxTime, endTime);
            validDatesFound = true;
          }
        }
      }

      if (!validDatesFound) return {};

      return { start: new Date(minTime), end: new Date(maxTime) };
    };

    const getMinMaxDates = (dates: Date[]): { start?: Date; end?: Date } => {
      // This version avoids filtering and mapping twice for min and max.
      let minTime = Infinity;
      let maxTime = -Infinity;

      for (const d of dates) {
        const time = d.getTime();
        if (!isNaN(time)) {
          minTime = Math.min(minTime, time);
          maxTime = Math.max(maxTime, time);
        }
      }

      if (minTime === Infinity) return {};

      return { start: new Date(minTime), end: new Date(maxTime) };
    };

    // The rest of the `buildTasks` function and its internal logic remains unchanged.
    // It will automatically benefit from the faster helper functions defined above.
    const buildTasks = (
      item: ProjectHierarchy | Program | Project,
      parentId = "",
      parentLevel = ""
    ): GanttTaskExtendedType[] => {
      // ... no changes to the internal logic of this function ...
      // All calls to `calculateDuration`, `getMinMaxDates`, etc., will now use the optimized versions.
      const isPortfolioItem = isPortfolio(item);
      const isProgramItem = isProgram(item);
      const isProjectItem = isProject(item);
      const id = isPortfolioItem
        ? item.portfolioId
        : isProgramItem
        ? item.programId
        : item.projectId;
      const name = isPortfolioItem
        ? item.portfolioName
        : isProgramItem
        ? item.programName
        : item.projectName;

      const subtasks: GanttTaskExtendedType[] = [];

      // Build subtasks recursively first
      if (isPortfolioItem) {
        item.programs?.forEach((program) => {
          subtasks.push(
            ...buildTasks(program, id, `${parentLevel}.${subtasks.length + 1}`)
          );
        });
        item.projects?.forEach((project) => {
          subtasks.push(
            ...buildTasks(project, id, `${parentLevel}.${subtasks.length + 1}`)
          );
        });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error: projects might not exist on Program type formally but used in hierarchy
      } else if (isProgramItem && item.projects?.length) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        item.projects.forEach((project) => {
          subtasks.push(
            ...buildTasks(project, id, `${parentLevel}.${subtasks.length + 1}`)
          );
        });
      }

      const baseColor =
        isPortfolioItem || isProgramItem ? "#3b82f6" : "#22c55e";

      // Helper to create a Gantt task object
      const createTask = (
        taskId: string,
        label: string,
        actualStartDay: Date, // Actual first day of task
        actualLastDay: Date, // Actual last day of task
        highlightColor: string,
        isComparison = false,
        duration?: number // Optional: pass calculated duration
      ): GanttTaskExtendedType => {
        // Ensure dates are valid before creating task
        if (
          !(
            actualStartDay instanceof Date && !isNaN(actualStartDay.getTime())
          ) ||
          !(actualLastDay instanceof Date && !isNaN(actualLastDay.getTime()))
        ) {
          console.warn(
            `Skipping task ${taskId}: Invalid dates (Start: ${actualStartDay}, End: ${actualLastDay})`
          );
          return null as any; // Return null or similar to indicate skip
        }

        const ganttEndDate = new Date(actualLastDay);
        ganttEndDate.setDate(ganttEndDate.getDate() + 1); // Add 1 day for Gantt library 'end' format
        ganttEndDate.setHours(0, 0, 0, 0); // Add 1 day for Gantt library 'end' format
        actualStartDay.setHours(0, 0, 0, 0);
        const calculatedDuration =
          duration ?? calculateDuration(actualStartDay, actualLastDay); // Calculate if not provided
        function calculateProgress(isProjectItem: boolean, taskId: string, item: any) {
          // Safe number parser — always returns a valid number
          const toNum = (val: any, fallback = 0) => {
            const num = parseFloat(val);
            return isFinite(num) ? num : fallback;
          };

          if (!isProjectItem) {
            return toNum(item?.progress, 0);
          }

          const progression = toNum(item?.progressionPercentage, 1) ; // fallback to 1 to avoid NaN/0
          const spi = toNum(item?.spi, 1);

          // Handle "-est" case
          if (taskId?.endsWith("-est")) {
            console.log({progression,spi});
            return progression !== 0 ? toNum((progression*100) / (spi/100)  , 0) : 0;
          }
          
          return toNum(progression * 100, 0);
        }

        const progress = calculateProgress(isProjectItem, taskId, item);

            
        return {
          createdByUserId: "", // Assuming default
          id: taskId,
          name: isComparison
            ? `${label} (${
                taskId.endsWith("-est")
                  ? t("Estimated")
                  : taskId.endsWith("-act")
                  ? t("Current") // Assuming 'Current' for actual
                  : taskId.endsWith("-recalc")
                  ? t("Recalculated")
                  : "Comparison" // Fallback label
              })`
            : `${label} ${
                taskId.endsWith("-est")
                  ? `(${t("Estimated")})`
                  : taskId.endsWith("-act")
                  ? `(${t("Current")})` // Assuming 'Current' for actual
                  : taskId.endsWith("-recalc")
                  ? `(${t("Recalculated")})`
                  : "" // Fallback label
              }`,
          type:
            isPortfolioItem || isProgramItem // Parent tasks type is 'project' for rollup visually
              ? "project"
              : item.isMilestone // Project item types
              ? "milestone"
              : "task",
          start: actualStartDay, // Convert actual start day to UTC
          end: ganttEndDate, // Convert day *after* last day to UTC
          duration: calculatedDuration,
          progress:progress, // Assuming progressionPercentage is a number between 0 and 1
          showProgress: isProjectItem, // Only show progress bar for projects
          project: parentId, // Parent relationship
          dependencies: [], // Assuming no dependencies in this conversion
          assign: isProjectItem // Only projects have assigned users directly
            ? item.assignedUsers?.map((u) => ({
                user: u.user,
                role: u.projectRole,
              })) // Assuming this structure
            : ([] as any),
          subtask:undefined, // Will be set later for parent tasks
          level: "", // Assuming level is set elsewhere or not used here
          status: isProjectItem ? item.status ?? "" : "", // Only projects have status
          flag: "", // Assuming no flags
          hideChildren:
            isPortfolioItem || isProgramItem
              ? !expanded?.includes(taskId)
              : false, // Hide children for expanded parents
          isDisabled: isProjectItem
            ? !(verification(item) && item?.isActive)
            : true,
          baseLineTask: [], // Will be set later
          styles: {
            // Using the base color and darkening for specific states
            backgroundColor: highlightColor, // Use the passed color
            backgroundSelectedColor: darkenColor(highlightColor, 0.2),
            progressColor: darkenColor(highlightColor, 0.15),
            progressSelectedColor: darkenColor(highlightColor, 0.3),
          },
        };
      };

      const taskList: GanttTaskExtendedType[] = [];

      // --- Handle Project Items (Leaf Nodes in this hierarchy) ---
      if (isProjectItem) {
        let mainStart: Date; // Actual first day
        let mainEndActualLastDay: Date; // Actual last day
        let mainDuration: number | undefined;

        const baseLines: GanttTaskExtendedType[] = [];

        // Determine main bar dates and create baseline tasks for the project
        switch (compare) {
          case "estimatedToActual":
            // Main task = estimated range
            // eslint-disable-next-line no-case-declarations
            const smartEst = getSmartEndDateAndDuration(item, "estimated");
            mainStart = smartEst.actualStartDay;
            mainEndActualLastDay = smartEst.actualLastDay;
            mainDuration = smartEst.duration;

            // Add Actual baseline
            // eslint-disable-next-line no-case-declarations
            const smartAct = getSmartEndDateAndDuration(item, "actual");
            // eslint-disable-next-line no-case-declarations
            const actualBaseline = createTask(
              `${id}-act`,
              name ?? "",
              smartAct.actualStartDay,
              smartAct.actualLastDay,
              "#FF69B4", // Pink for Actual
              true,
              smartAct.duration
            );
            if (actualBaseline) baseLines.push(actualBaseline);
            break;

          case "actualToReCalculated":
            // Main task = actual range
            const smartActMain = getSmartEndDateAndDuration(item, "actual");
            mainStart = smartActMain.actualStartDay;
            mainEndActualLastDay = smartActMain.actualLastDay;
            mainDuration = smartActMain.duration;

            // Determine Recalculated baseline dates
            const recalcBaseDate = item.actualStartDate
              ? new Date(item.actualStartDate)
              : new Date(item.startDate); // Base start from actual or project start
            const { start: recalcStart, end: recalcEndActualLastDay } =
              ensureDatesValid(
                recalcBaseDate,
                item.reCalculateEndDate
                  ? new Date(item.reCalculateEndDate)
                  : null // Use recalc end if exists, otherwise potentially invalid
              );

            // Create Recalculated baseline
            const recalculatedBaseline = createTask(
              `${id}-recalc`,
              name ?? "",
              recalcStart,
              recalcEndActualLastDay,
              "#3b82f6", // Blue for Recalculated
              true,
              item.reCalculatedDuration ??
                calculateDuration(recalcStart, recalcEndActualLastDay) // Use recalc duration if available, else calculate
            );
            if (recalculatedBaseline) baseLines.push(recalculatedBaseline);
            break;

          case "all":
            // Main task = estimated range
            const smartEstAll = getSmartEndDateAndDuration(item, "estimated");
            mainStart = smartEstAll.actualStartDay;
            mainEndActualLastDay = smartEstAll.actualLastDay;
            mainDuration = smartEstAll.duration;

            // Add Actual baseline
            const smartActAll = getSmartEndDateAndDuration(item, "actual");
            const actualBaselineAll = createTask(
              `${id}-act`,
              name ?? "",
              smartActAll.actualStartDay,
              smartActAll.actualLastDay,
              "#FF69B4", // Pink for Actual
              true,
              smartActAll.duration
            );
            if (actualBaselineAll) baseLines.push(actualBaselineAll);

            // Add Recalculated baseline if exists, otherwise fallback logic similar to actualToReCalculated case
            const recalcBaseDateAll = item.actualStartDate
              ? new Date(item.actualStartDate)
              : new Date(item.startDate);
            const { start: recalcStartAll, end: recalcEndActualLastDayAll } =
              ensureDatesValid(
                recalcBaseDateAll,
                item.reCalculateEndDate
                  ? new Date(item.reCalculateEndDate)
                  : null // Use recalc end if exists
              );
            // eslint-disable-next-line no-case-declarations
            const recalculatedBaselineAll = createTask(
              `${id}-recalc`,
              name ?? "",
              recalcStartAll,
              recalcEndActualLastDayAll,
              "#3b82f6", // Blue for Recalculated
              true,
              item.reCalculatedDuration ??
                calculateDuration(recalcStartAll, recalcEndActualLastDayAll)
            );
            if (recalculatedBaselineAll)
              baseLines.push(recalculatedBaselineAll);

            break;

          default: // "none" or any other value
            // Main task = selectedDateType range
            // eslint-disable-next-line no-case-declarations
            const smartDefault = getSmartEndDateAndDuration(
              item,
              selectedDateType
            );
            mainStart = smartDefault.actualStartDay;
            mainEndActualLastDay = smartDefault.actualLastDay;
            mainDuration = smartDefault.duration;
            break;
        }

        // Ensure main task dates are valid before creating
        const { start: finalMainStart, end: finalMainEndActualLastDay } =
          ensureDatesValid(mainStart, mainEndActualLastDay);

        // Create the main task for the project item
        const mainTask = createTask(
          `${selectedDateType === "estimated" ? `${id}-est` : `${id}-act`}`,
          name ?? "",
          finalMainStart,
          finalMainEndActualLastDay,
          selectedDateType == "actual" && !compare ? "#FF69B4" :compare == "actualToReCalculated" ? "#FF69B4" :baseColor, // Use pink for actual main bar when no comparison
          false, // This is the main bar
          mainDuration
        );

        if (mainTask) {
          // Only push if main task was successfully created (dates were valid)
          mainTask.baseLineTask = baseLines; // Assign baselines to the main task
          taskList.push(mainTask);
        }
      } else {
        // --- Handle Non-Project Items (Portfolio or Program Parents) ---

        const parentBaseLines: GanttTaskExtendedType[] = [];

        // Aggregate dates from children's tasks to create parent baselines
        if (subtasks.length > 0 && compare) {
          // Filter children's tasks by type (main bar vs baseline)
          // Note: BaseLineTask array contains the comparison bars for projects.
          // We need to collect all actual and recalculated baseline tasks from children's baseLineTask arrays.
          const allChildActualBaselines: GanttTaskExtendedType[] = [];
          const allChildRecalculatedBaselines: GanttTaskExtendedType[] = [];
          const allChildMainTasks: GanttTaskExtendedType[] = [];

          subtasks.forEach((childTask) => {
            if (isProject(childTask)) {
              // Process only project type children for baselines
              allChildMainTasks.push(childTask); // Add project's main bar
              if (Array.isArray(childTask.baseLineTask)) {
                (childTask.baseLineTask as GanttTaskExtendedType[]).forEach(
                  (childBaseline) => {
                    if (childBaseline.id.endsWith("-act")) {
                      allChildActualBaselines.push(childBaseline);
                    } else if (childBaseline.id.endsWith("-recalc")) {
                      allChildRecalculatedBaselines.push(childBaseline);
                    }
                    // Note: Estimated baselines are typically the main bar when needed for comparison start point
                  }
                );
              }
            } else {
              // If the child is a parent task (Program/Portfolio), include its own baselines
              // in the aggregation for this higher-level parent.
              allChildMainTasks.push(childTask); // Add child parent's main bar
              if (Array.isArray(childTask.baseLineTask)) {
                (childTask.baseLineTask as GanttTaskExtendedType[]).forEach(
                  (childBaseline) => {
                    if (childBaseline.id.endsWith("-act")) {
                      allChildActualBaselines.push(childBaseline);
                    } else if (childBaseline.id.endsWith("-recalc")) {
                      allChildRecalculatedBaselines.push(childBaseline);
                    }
                  }
                );
              }
            }
          });

          // Aggregate dates from these filtered lists
          // const { start: minMainStart, end: maxMainEnd } = getMinMaxDatesFromTasks(allChildMainTasks); // Range of children's main bars
          const { start: minActStart, end: maxActEnd } =
            getMinMaxDatesFromTasks(allChildActualBaselines); // Range of children's actual baselines
          const { start: minRecalcStart, end: maxRecalcEnd } =
            getMinMaxDatesFromTasks(allChildRecalculatedBaselines); // Range of children's recalculated baselines

          // Helper to create a parent baseline task
          const createParentBaseline = (
            prefix: string,
            start?: Date, // Actual first day of the range
            end?: Date, // Actual last day of the range
            color = baseColor
          ): GanttTaskExtendedType | null => {
            // Ensure dates are valid for a baseline task
            const { start: actualStartDay, end: actualLastDay } =
              ensureDatesValid(
                start ?? new Date("invalid date"), // Provide invalid date if start is missing
                end ?? new Date("invalid date") // Provide invalid date if end is missing
              );

            if (
              !(
                actualStartDay instanceof Date &&
                !isNaN(actualStartDay.getTime())
              ) ||
              !(
                actualLastDay instanceof Date && !isNaN(actualLastDay.getTime())
              )
            ) {
              // console.warn(`Skipping parent baseline ${id}-${prefix}: Invalid aggregated dates`);
              return null; // Skip creating the baseline if the aggregated range is invalid
            }

            const duration = calculateDuration(actualStartDay, actualLastDay);

            return createTask(
              `${id}-${prefix}`,
              name ?? "", // Use parent's name
              actualStartDay, // Actual first day
              actualLastDay, // Actual last day
              color,
              true, // It's a comparison task
              duration
            );
          };

          // Create parent baselines based on the comparison mode and aggregated child dates
          switch (compare) {
            case "estimatedToActual":
              // Parent main bar will represent the range of children's estimated/main bars (minMainStart/maxMainEnd)
              // Add Parent Actual baseline
              const actualParentBaseline = createParentBaseline(
                "act",
                minActStart,
                maxActEnd,
                "#FF69B4"
              );
              if (actualParentBaseline)
                parentBaseLines.push(actualParentBaseline);
              break;

            case "actualToReCalculated":
              // Parent main bar will represent the range of children's actual bars (minMainStart/maxMainEnd)
              // Add Parent Recalculated baseline
              const recalculatedParentBaseline = createParentBaseline(
                "recalc",
                minRecalcStart,
                maxRecalcEnd,
                "#22c55e"
              );
              if (recalculatedParentBaseline)
                parentBaseLines.push(recalculatedParentBaseline);
              break;

            case "all":
              // Parent main bar will represent the range of children's estimated/main bars (minMainStart/maxMainEnd)
              // Add Parent Actual and Recalculated baselines
              const actualParentBaselineAll = createParentBaseline(
                "act",
                minActStart,
                maxActEnd,
                "#FF69B4"
              );
              if (actualParentBaselineAll)
                parentBaseLines.push(actualParentBaselineAll);
              const recalculatedParentBaselineAll = createParentBaseline(
                "recalc",
                minRecalcStart,
                maxRecalcEnd,
                "#22c55e"
              );
              if (recalculatedParentBaselineAll)
                parentBaseLines.push(recalculatedParentBaselineAll);
              // Note: We don't create an 'estimated' baseline for the parent when the main bar is estimated,
              // following the pattern of the project level.
              break;

            // default: "none" comparison is handled by parentBaseLines being empty initially
          }
        }

        // Calculate start/end for the main parent task
        // This task spans the entire range of its children's *main* bars and their baselines
        const allDates: Date[] = [];
        subtasks.forEach((t) => {
          if (t.start) {
            const start = new Date(t.start);
            if (start instanceof Date && !isNaN(start.getTime()))
              allDates.push(start);
          }
          // Need the actual last day from the task's end property for aggregation
          if (t.end) {
            const actualLastDay = new Date(t.end);
            actualLastDay.setDate(actualLastDay.getDate() - 1); // Subtract 1 day
            if (
              actualLastDay instanceof Date &&
              !isNaN(actualLastDay.getTime())
            )
              allDates.push(actualLastDay);
          }

          // Include dates from children's baselines too for the overall parent span
          if (Array.isArray(t.baseLineTask)) {
            t.baseLineTask.forEach((b) => {
              if (b.start) {
                const start = new Date(b.start);
                if (start instanceof Date && !isNaN(start.getTime()))
                  allDates.push(start);
              }
              if (b.end) {
                const actualLastDay = new Date(b.end);
                actualLastDay.setDate(actualLastDay.getDate() - 1); // Subtract 1 day
                if (
                  actualLastDay instanceof Date &&
                  !isNaN(actualLastDay.getTime())
                )
                  allDates.push(actualLastDay);
              }
            });
          }
        });

        // Get the overall min/max date range for the parent main bar
        const { start: overallMinStart, end: overallMaxEnd } =
          getMinMaxDates(allDates);

        // Ensure the overall range is valid before creating the task
        const { start: parentStartDay, end: parentEndActualLastDay } =
          ensureDatesValid(
            overallMinStart ?? new Date("invalid date"), // Use minStart if available, otherwise invalid
            overallMaxEnd ?? null // maxEnd can be null if allDates is empty
          );

        // Calculate duration for the main parent bar
        const parentDuration = calculateDuration(
          parentStartDay,
          parentEndActualLastDay
        );

        // Create the main parent task (rollup)
        const parentTask = createTask(
          id ?? "",
          name ?? "",
          parentStartDay, // Actual first day of the parent span
          parentEndActualLastDay, // Actual last day of the parent span
          baseColor, // Use the parent's base color for the main bar
          false, // It's the main bar
          parentDuration
        );

        if (parentTask) {
          // Only push if parent task was successfully created (dates were valid)
          if (subtasks.length) parentTask.subtask = subtasks; // Assign children as subtasks
          parentTask.baseLineTask = parentBaseLines; // Assign the calculated parent baselines
          taskList.push(parentTask);
        }
      } // End of non-Project Item handling

      return taskList; // Return the list of tasks created at this level
    }; // End of buildTasks function

    const builtTasks = hierarchy.flatMap((h, index) => {
      const tasks = buildTasks(h, "", `${index + 1}`);
      return tasks.filter((task) => task !== null);
    });

    const finalTasks = sortAndFlattenTask(builtTasks);

    return finalTasks;
  };

  const sortAndFlattenTask = (
    tasks: GanttTaskExtendedType[],
    parentLevel = ""
  ): GanttTaskExtendedType[] => {
    let result: GanttTaskExtendedType[] = [];

    tasks.forEach((task, index) => {
      const level = parentLevel
        ? `${parentLevel}.${index + 1}`
        : `${index + 1}`;
      result.push({ ...task, level });

      if (task.subtask?.length) {
        result = result.concat(sortAndFlattenTask(task.subtask, level));
      }
    });

    return result;
  };

  const nonWorkingDays = useCallback(() => {
    const nWd =
      user?.userOrganisation.map((org) => org.organisation.nonWorkingDays) ||
      [];
    const mapDayToNumber = (day: OrgListOfNonWorkingDaysEnum) => {
      switch (day) {
        case "SUN":
          return 0;
        case "MON":
          return 1;
        case "TUE":
          return 2;
        case "WED":
          return 3;
        case "THU":
          return 4;
        case "FRI":
          return 5;
        case "SAT":
          return 6;
        default:
          return 0;
      }
    };
    const result: number[] = [];
    nWd.forEach((days) => {
      days.forEach((day) => {
        result.push(mapDayToNumber(day));
      });
    });
    return result;
  }, [user]);

  const UTCDateConverter = (date: Date) => {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
    );
  };

  // Using your existing duration calculation for business logic consistency
  const calculateDurations = useCallback(
    (startDate: Date, endDate: Date): number => {
      const start = UTCDateConverter(new Date(startDate));
      const end = UTCDateConverter(new Date(endDate));

      end.setDate(end.getDate() + 1);
      const excludedDays = nonWorkingDays();
      const holidays =
        user?.userOrganisation?.[0]?.organisation?.orgHolidays?.map((h) =>
          dateFormater(new Date(h.holidayStartDate))
        ) ?? [];

      let duration = 0;
      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        const day = d.getDay();
        const formattedDate = d.toISOString().split("T")[0];
        if (
          !excludedDays.includes(day) &&
          !holidays.includes(dateFormater(new Date(formattedDate)))
        ) {
          duration++;
        }
      }
      return duration;
    },
    [user, nonWorkingDays]
  );

  /**
   * Calculates progress metrics for a single project using the component's duration logic.
   */
  const calculateProjectMetrics = useCallback(
    (project: Project) => {
      const { actualStartDate, actualEndDate, progressionPercentage } = project;

      if (!actualStartDate || !actualEndDate) {
        return { duration: 0, weightedProgress: 0, progress: 0 };
      }

      const duration = calculateDurations(
        new Date(actualStartDate),
        new Date(actualEndDate)
      );
      const progress = Math.round(Number(progressionPercentage || 0) * 100);
      const weightedProgress = progress * duration;

      return { duration, weightedProgress, progress };
    },
    [calculateDurations]
  );

  /**
   * Builds a complete project hierarchy including portfolios, orphan programs,
   * and standalone projects, with weighted progress calculated at each level.
   */
  const buildCompleteHierarchy = useCallback(
    (projectsList: Project[]): HierarchyItem[] => {
      const portfolioMap = new Map<string, Calculable<PortfolioNode>>();
      const orphanProgramMap = new Map<string, Calculable<ProgramNode>>();
      const standaloneProjects: ProjectNode[] = [];

      for (const project of projectsList) {
        if (!project.projectId) continue;

        const { duration, weightedProgress, progress } =
          calculateProjectMetrics(project);
        const projectNode: ProjectNode = {
          ...project,
          type: "project",
          progress,
        };

        const hasPortfolio = !!project.portfolio?.portfolioId;
        const hasProgram = !!project.program?.programId;

        if (hasPortfolio) {
          const portfolioId = project.portfolio!.portfolioId;
          if (!portfolioMap.has(portfolioId)) {
            portfolioMap.set(portfolioId, {
              type: "portfolio",
              portfolioId,
              portfolioName:
                project.portfolio!.portfolioName || "Unnamed Portfolio",
              progress: 0,
              projects: [],
              programs: [],
              __totalDuration: 0,
              __weightedProgress: 0,
            });
          }
          const portfolioEntry = portfolioMap.get(portfolioId)!;
          portfolioEntry.__totalDuration += duration;
          portfolioEntry.__weightedProgress += weightedProgress;

          if (hasProgram) {
            const programId = project.program!.programId;
            let programEntry = portfolioEntry.programs.find(
              (p) => p.programId === programId
            ) as Calculable<ProgramNode> | undefined;

            if (!programEntry) {
              programEntry = {
                type: "program",
                programId,
                programName: project.program!.programName || "Unnamed Program",
                progress: 0,
                projects: [],
                __totalDuration: 0,
                __weightedProgress: 0,
              };
              portfolioEntry.programs.push(programEntry as ProgramNode);
            }

            programEntry.__totalDuration += duration;
            programEntry.__weightedProgress += weightedProgress;
            programEntry.projects.push(projectNode);
          } else {
            portfolioEntry.projects.push(projectNode);
          }
        } else if (hasProgram) {
          const programId = project.program!.programId;
          if (!orphanProgramMap.has(programId)) {
            orphanProgramMap.set(programId, {
              type: "program",
              programId,
              programName: project.program!.programName || "Unnamed Program",
              progress: 0,
              projects: [],
              __totalDuration: 0,
              __weightedProgress: 0,
            });
          }
          const programEntry = orphanProgramMap.get(programId)!;
          programEntry.__totalDuration += duration;
          programEntry.__weightedProgress += weightedProgress;
          programEntry.projects.push(projectNode);
        } else {
          standaloneProjects.push(projectNode);
        }
      }

      const finalPortfolios = Array.from(portfolioMap.values()).map((p) => {
        p.programs.forEach((prog) => {
          const program = prog as Calculable<ProgramNode>;
          program.progress =
            program.__totalDuration > 0
              ? Math.round(program.__weightedProgress / program.__totalDuration)
              : 0;
          delete (program as any).__totalDuration;
          delete (program as any).__weightedProgress;
        });
        p.progress =
          p.__totalDuration > 0
            ? Math.round(p.__weightedProgress / p.__totalDuration)
            : 0;
        delete (p as any).__totalDuration;
        delete (p as any).__weightedProgress;
        return p;
      });

      const finalOrphanPrograms = Array.from(orphanProgramMap.values()).map(
        (p) => {
          p.progress =
            p.__totalDuration > 0
              ? Math.round(p.__weightedProgress / p.__totalDuration)
              : 0;
          delete (p as any).__totalDuration;
          delete (p as any).__weightedProgress;
          return p;
        }
      );

      return [
        ...finalPortfolios,
        ...finalOrphanPrograms,
        ...standaloneProjects,
      ];
    },
    [calculateProjectMetrics]
  );

  // 3. UPDATED useEffect TO USE THE NEW LOGIC

  const expandToggle = (id: string) => {
    if (expanded?.includes(id)) {
      setExpanded(expanded.filter((t) => t !== id));
    } else {
      setExpanded([...(expanded ?? []), id]);
    }
  };
  const Divref = useRef<HTMLDivElement | null>(null);
  const GanttHeight = useRef<number>(600);

  const setGanttHeight =(num : number)=>{
    GanttHeight.current = num
  }
  const updateHeight = () => {
    setGanttHeight(
      Divref.current?.offsetHeight ? Divref.current.offsetHeight - 70 : 600
    );
  };
  useEffect(() => {
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (Divref.current) {
      resizeObserver.observe(Divref.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [Divref.current?.offsetHeight]);

  const viewModes = [
    ViewMode.Day,
    ViewMode.Week,
    ViewMode.Month,
    ViewMode.QuarterYear,
    ViewMode.Year,
  ];
  const minZoom = 0;
  const maxZoom = viewModes.length - 1;

  const zoominout = (num: number) => {
    // Ensure zoom level is within bounds
    const newZoomState = Math.max(minZoom, Math.min(num, maxZoom));
    setZoomState(newZoomState);
    setView(viewModes[newZoomState]);
  };

  const [ganttDate, setGanttDate] = useState({
    start: new Date(),
    end: new Date(),
  });

  useEffect(() => {
    const project = projects.sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
    setGanttDate({
      start: UTCDateConverter(new Date(project[0].startDate)),
      end: UTCDateConverter(
        new Date(project[project.length - 1].actualEndDate)
      ),
    });
  }, [projects]);

  useEffect(() => {
    zoominout(FilterUnit);
  }, [FilterUnit]);
  useEffect(() => {
    setFilterUnit(zoomState);
  }, [zoomState]);
  useEffect(() => {
    let timeoutId: number;
    const handleScroll = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault();
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (event.deltaY < 0 && zoomState > minZoom) {
            zoominout(zoomState - 1);
            // Decrement zoom
          } else if (event.deltaY > 0 && zoomState < maxZoom) {
            zoominout(zoomState + 1); // Increment zoom
          }
        }, 100); // Adjust debounce delay as needed
      }
    };

    const div = Divref.current;
    if (div) {
      div.addEventListener("wheel", handleScroll);
    }

    return () => {
      if (div) {
        div.removeEventListener("wheel", handleScroll);
      }
      clearTimeout(timeoutId);
    };
  }, [zoomState, viewModes, minZoom, maxZoom]);
  const projectQuery = useProjectQuery(false);
  const projectUpdateMutation = useProjectUpdateMutation("");
  const projectUpdate = (updatedData: GanttTaskExtendedType) => {
    const newStartDate = UTCDateConverter(updatedData.start);
    const newEndDate = UTCDateConverter(updatedData.end);

    const current = data.find((p) => p.projectId === updatedData.id);
    if (!current) return;

    const currentStart = new Date(current.startDate);
    const currentEnd = current.estimatedEndDate
      ? new Date(current.estimatedEndDate)
      : currentStart;

    const payload: any = { projectId: updatedData.id };
    const isStartDateUpdated =
      currentStart.toISOString() !== newStartDate.toISOString();
    const isEndDateUpdated =
      currentEnd.toISOString() !== newEndDate.toISOString();
    if (isStartDateUpdated) {
      const durationInDays = Math.ceil(
        (currentEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60 * 24)
      );
      const adjustedEnd = new Date(newStartDate);
      adjustedEnd.setDate(adjustedEnd.getDate() + durationInDays);

      payload.startDate = newStartDate;
      payload.estimatedEndDate = adjustedEnd;
    } else if (isEndDateUpdated) {
      payload.startDate = currentStart;
      const adjustedEnd = new Date(newEndDate);
      adjustedEnd.setDate(adjustedEnd.getDate() - 1); // Backend expects one day less
      adjustedEnd.setHours(0, 0, 0, 0);
      payload.estimatedEndDate = adjustedEnd;
    }
    payload.startDate.setHours(0, 0, 0, 0);
    payload.estimatedEndDate.setHours(0, 0, 0, 0);
    // Local update of state
    if (payload.startDate || payload.estimatedEndDate) {
      setData((prev) =>
        prev.map((project) =>
          project.projectId === updatedData.id
            ? {
                ...project,
                ...(isStartDateUpdated && {
                  startDate: payload.startDate.toISOString(),
                }),
                ...(isEndDateUpdated && {
                  estimatedEndDate: payload.estimatedEndDate.toISOString(),
                }),
              }
            : project
        )
      );

      const apiPayload = {
        ...payload,
        startDate: payload.startDate,
        estimatedEndDate: payload.estimatedEndDate,
      };

      projectUpdateMutation.mutate(apiPayload, {
        onSuccess() {
          projectQuery.refetch().then((res) => {
            setData(
              res.data?.data.data.sort(
                (a, b) =>
                  new Date(a.startDate).getTime() -
                  new Date(b.startDate).getTime()
              ) ?? []
            );
          });
        },
        onError(error) {
          const err = error.response?.data;
          toast.error(
            errorKeyFormate(err?.message ?? "Unknown error"),
            errorConfig
          );
        },
      });
    }
  };
  const [convertedTasks, setConvertedTasks] = useState<GanttTaskExtendedType[]>(
    []
  );
  useMemo(
    () => setConvertedTasks(convertProject(data)),
    [data, expanded, selectedDateType, compare]
  );

  useEffect(() => {
    const tempV: string[] = [];

    if (expandAll) {
      convertedTasks.forEach((task) => {
        if (task.type === "project") {
          tempV.push(task.id);
        }
      });
    }
    const hasChanged =
      expanded?.length !== tempV.length ||
      !expanded?.every((id, index) => id === tempV[index]);
    if (hasChanged) {
      setExpanded(tempV);
    }
  }, [expandAll]);

  const projectDuplicateMutation = useProjectDuplicateMutation();
  const { validatePlan } = usePlanValidation();
  const [projectId, setProjectId] = useState<string>();
  const [remove, setRemove] = useState<string>();
  const removeProject = () => {
    removeProjectMutation.mutate(remove ?? "", {
      onSuccess(data) {
        toast.success(data.data.message);
        projectQuery.refetch();
        setRemove(undefined);
      },
      onError(error) {
        toast.error(errorKeyFormate(error.response?.data.message + ""));
      },
    });
  };

  const contextmanu: React.FC<{
    task: any;
    fontSize: string;
    fontFamily: string;
  }> = ({ task }) => {
    if (task.type == "project") {
      return <></>;
    }
    const findUser = task?.assign?.find(
      (t: { user: { userId: string | undefined } }) =>
        t.user.userId == user?.userId
    );

    if (findUser && findUser.role == UserRoleEnumValue.COLLABORATOR) {
      return <></>;
    }

    const createDuplicate = (id: string) => {
      if (!validatePlan("project").valid) {
        return;
      }
      projectDuplicateMutation.mutate(id, {
        onSuccess(data) {
          toast.success(data.data.message);
          projectQuery.refetch();
        },
        onError(error) {
          toast.error(
            errorKeyFormate(error.response?.data.message + ""),
            errorConfig
          );
        },
      });
    };
    const handleView = (id: string) => {
      navigate("/project-details/" + id);
    };
    const projectId = task.id.endsWith("-est")
      ? task.id.split("-est")[0]
      : task.id.endsWith("-act")
      ? task.id.split("-act")[0]
      : task.id.endsWith("-recalc")
      ? task.id.split("-recalc")[0]
      : task.id;
    return (
      <div className="w-fit flex flex-col gap-2 bg-white shadow-md rounded-md p-2">
        <hr className="mx-1" />

        <div
          className="dropdown-menu-item flex cursor-pointer items-center"
          onClick={() => handleView(projectId)}
        >
          <Settings className="mr-2 h-4 w-4 text-[#44546F]" />
          <span className="p-0 font-normal h-auto">Project Settings</span>
        </div>

        <div
          className="dropdown-menu-item flex cursor-pointer items-center"
          onClick={() => setProjectId(projectId)}
        >
          <ScrollText className="mr-2 h-4 w-4 text-[#44546F]" />
          <span className="p-0 font-normal h-auto">Create Task</span>
        </div>

        {user?.userOrganisation[0]?.role == UserRoleEnumValue.ADMINISTRATOR && (
          <div
            className="dropdown-menu-item flex cursor-pointer items-center"
            onClick={() => createDuplicate(projectId)}
          >
            <img src={DuplicateIcon} className="h-4 w-4 mr-2" />
            <span className="p-0 font-normal h-auto">Duplicate Project</span>
          </div>
        )}

        <Link
          className="dropdown-menu-item flex cursor-pointer items-center"
          to={"/members/" + projectId}
        >
          <img src={Users} className="h-4 w-4 mr-2" />
          <span className="p-0 font-normal h-auto">Project Members</span>
        </Link>

        {user?.userOrganisation[0]?.role == UserRoleEnumValue.ADMINISTRATOR && (
          <div
            className="dropdown-menu-item flex cursor-pointer items-center"
            onClick={() => setRemove(projectId)}
          >
            <img src={TrashCan} className="h-4 w-4 mr-2" />
            <span className="p-0 font-normal h-auto text-red-500">
              Remove Project
            </span>
          </div>
        )}
      </div>
    );
  };
  const StandardTooltipContent: React.FC<{
    task: any;
    fontSize: string;
    fontFamily: string;
  }> = ({ task }) => {
    const taskType = task.id.endsWith("-est")
      ? "Estimated"
      : task.id.endsWith("-act")
      ? "Actual"
      : task.id.endsWith("-recalc")
      ? "Recalculated"
      : "Comparison";

    let startDate: Date;
    let endDate: Date;
    let duration: number = 1;
    const project = projects.find((t) =>
      String(task.id).includes(t.projectId)
    ) as Project;
    switch (taskType) {
      case "Estimated":
        startDate = new Date(task.startDate ?? task.start);
        endDate = new Date(task.estimatedEndDate ?? task.end);
        endDate.setDate(endDate.getDate() - 1);
        duration = project?.estimatedDuration ?? 1;
        break;
      case "Actual":
        startDate = new Date(task.actualStartDate ?? task.start);
        endDate = new Date(task.actualEndDate ?? task.end);
        endDate.setDate(endDate.getDate() - 1);
        duration = project?.actualDuration ?? 1;
        break;
      case "Recalculated":
        startDate = new Date(task.actualStartDate ?? task.start);
        endDate = new Date(task.reCalculateEndDate ?? task.end);
        endDate.setDate(endDate.getDate() - 1);
        duration = project?.reCalculatedDuration ?? 1;
        break;
      default:
        startDate = new Date(task.start);
        endDate = new Date(task.end);
        endDate.setDate(endDate.getDate() - 1);
        duration =
          selectedDateType == "actual"
            ? project?.actualDuration ?? 1
            : project?.estimatedDuration ?? 1;
    }
    if (task.type == "project") {
      duration = task.duration ?? 1;
    }

    // Adjust for inclusive display
    let displayEndDate = new Date(endDate);
    if ((task.duration > 0 ? task.duration : 1) == 1) {
      displayEndDate = startDate;
    }

    return (
      <div className="relative  w-60 max-w-[250px]">
        <h3 className="text-lg font-semibold mb-2">{task.name}</h3>
        {taskType !== "Comparison" && (
          <p className="text-sm text-gray-500 italic mb-2">Type: {taskType}</p>
        )}

        <div className="flex justify-between text-sm">
          <span>{t("start_date")}:</span>
          <span>{dateFormater(startDate)}</span>
        </div>

        <div className="flex justify-between text-sm">
          <span>{t("end_date")}:</span>
          <span>{dateFormater(displayEndDate)}</span>
        </div>

        <div className="flex justify-between text-sm">
          <span>{t("Duration")}:</span>
          <span>{duration > 0 ? Number(duration).toFixed() : 1} days</span>
        </div>
        <div className="flex justify-between items-center text-nowrap text-sm mt-0">
          <span>{taskType=="Estimated"?t("Expected progress"): taskType=="Actual"?t("Current progress"):t("progress")}:</span>
          <div className="w-full bg-[#78748649] h-2 rounded-lg overflow-hidden ml-3">
            <div
              className="bg-[#299d91] h-full"
              style={{ width: `${task.progress}%` }}
            ></div>
          </div>
          <span className="ml-2">{task.progress?.toFixed()}%</span>
        </div>
      </div>
    );
  };
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem("projects_hidden_columns");
      console.log({ stored });

      if (!stored) {
        localStorage.setItem(
          "projects_hidden_columns",
          JSON.stringify([
            "actual_start_date",
            "actual_end_date",
            "Re_calculated_end_date",
            "spi",
            "cpi",
            "estimated_budget",
            "consumed_budget",
            "Re-calculated_Budget",
          ])
        );
      }
      return stored
        ? JSON.parse(stored)
        : [
            "actual_start_date",
            "actual_end_date",
            "Re_calculated_end_date",
            "spi",
            "cpi",
            "estimated_budget",
            "consumed_budget",
            "Re-calculated_Budget",
          ];
    } catch {}
  });
  const [sortCol, setSortCol] = useState<any>([]);

  const uniqueHiddenColumns = useMemo(
    () => Array.from(new Set(hiddenColumns)),
    [hiddenColumns, sortCol]
  );

  function sanitizeProjectName(name: string): string {
    const labels = [`(${t("Estimated")})`, `(${t("Current")})`];

    let cleaned = name;

    for (const label of labels) {
      const firstIndex = cleaned.indexOf(label);

      if (firstIndex !== -1) {
        // Remove ONLY the first occurrence
        cleaned =
          cleaned.slice(0, firstIndex) +
          cleaned.slice(firstIndex + label.length);

        cleaned = cleaned.trim();
      }
    }

    // Normalize extra spaces
    return cleaned.replace(/\s+/g, " ").trim();
  }

  const allCols = useMemo(() => {
    const col: columnsType[] = [
      {
        field: "name",
        name: t("project_name"),
        sortable: true,
        width: 300,
        render(data: any) {
          return (
            <div
              key={data.flag}
              className={`${
                data.project ? "" : ""
              } text-base !truncate flex items-center gap-1 w-full`}
            >
              {/* {data.flag == "Green" ? (
                              <img src={GreenFlag} className="h-3 w-3" />
                            ) : data.flag == "Red" ? (
                              <img src={RedFlag} className="h-3 w-3" />
                            ) : data.flag == "Orange" ? (
                              <img src={OrangeFlag} className="h-3 w-3" />
                            ) : (
                              ""
                            )} */}
              <div className="!truncate w-full">
                {sanitizeProjectName(data.name)}
              </div>
            </div>
          );
        },
      },
      {
        field: "assign",
        name: t("Project Manager"),
        width: 200,
        render(data: any) {
          const userPm = data.assign?.find((u: any) => {
            return u?.role == UserRoleEnumValue.PROJECT_MANAGER;
          });
          return userPm ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="ml-3 flex">
                    <div>
                      <UserAvatar
                        className="shadow-sm shadow-gray-300 border-2 border-white"
                        user={userPm?.user}
                      />
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm">
                    {userPm?.user.firstName && userPm?.user.lastName
                      ? `${userPm?.user.firstName} ${userPm?.user.lastName}`
                      : userPm?.user.email}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <></>
          );
        },
      },
      {
        field: "start",
        sortable: true,
        name: t("start_date"),
        width: 120,
        render(data: any) {
          return (
            <div className="w-full flex justify-center items-center">
              {dateFormater(data.start)}
            </div>
          );
        },
      },
      {
        field: "end",
        sortable: true,
        name: t("end_date"),
        width: 120,
        render(data: any) {
          return (
            <div className="w-full flex justify-center items-center">
              {dateFormater(data.end)}
            </div>
          );
        },
      },
      {
        field: "progress",
        name: t("progress"),
        sortable: true,
        width: 120,
        render(data: any) {
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);

          let spiColor = "#68B266";
          if (
            project &&
            typeof project?.spi === "number" &&
            project?.tasks?.length > 0
          ) {
            if (project && typeof project?.spi === "number") {
              if (typeof project.spi === "number") {
                const uniqueFlags = new Set(project.tasks.map((t) => t.flag));

                if (uniqueFlags.size === 1) {
                  const onlyFlag = uniqueFlags.values().next().value;
                  const flagColorMap: Record<string, string> = {
                    Green: "#68B266",
                    Red: "#EA4335",
                    Orange: "#FFA500", // Or "Orange" if that's the correct flag
                  };

                  spiColor = flagColorMap[onlyFlag as string] ?? "#68B266"; // Default to green if flag is unrecognized
                } else {
                  const spi = project.spi;

                  spiColor =
                    spi < 80
                      ? "#EA4335" // Red
                      : spi < 95
                      ? "#FFA500" // Orange
                      : "#68B266"; // Green
                }
              }
            }
          } else {
            if (project && typeof project?.spi === "number") {
              const todayDate = new Date();
              const projectStartDate = new Date(project.startDate ?? "");
              if (projectStartDate < todayDate) {
                spiColor = "#EA4335";
              }
            } else {
              const spi = spiPortfolioAndProgram
                ? spiPortfolioAndProgram[data.id]
                : 0;

              spiColor =
                spi < 80
                  ? "#EA4335" // Red
                  : spi < 95
                  ? "#FFA500" // Orange
                  : "#68B266"; // Green
              // Default color for non-project items
            }
          }

          return (
            <div className="flex justify-center w-full">
              <PercentageCircle
                percentage={data.progress}
                roundColor={spiColor}
              />
            </div>
          );
        },
      },
      {
        field: "estimated_duration",
        name: t("estimated_duration"),
        width: 120,
        render(data: any) {
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);

          return (
            <div className="w-full flex justify-center items-center">
              {project?.estimatedDuration && project?.estimatedDuration >= 0
                ? Number(project?.estimatedDuration).toFixed()
                : "-"}
            </div>
          );
        },
      },
      {
        field: "actual_duration",
        name: t("actual_duration"),
        width: 120,
        render(data: any) {
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);

          return (
            <div className="w-full flex justify-center items-center">
              {project?.actualDuration && project?.actualDuration >= 0
                ? Number(project?.actualDuration).toFixed()
                : "-"}
            </div>
          );
        },
      },
      {
        field: "Re_calculated_duration",
        name: t("Re_calculated_duration"),
        width: 120,
        render(data: any) {
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);
          return (
            <div className="w-full flex justify-center items-center">
              {project?.reCalculatedDuration &&
              project?.reCalculatedDuration >= 0
                ? Number(project?.reCalculatedDuration).toFixed()
                : "-"}
            </div>
          );
        },
      },
      {
        field: "actual_start_date",
        name: t("actual_start_date"),
        width: 120,
        render(data: any) {
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);
          return (
            <div className="w-full flex justify-center items-center">
              {project?.actualStartDate
                ? dateFormater(new Date(project?.actualStartDate))
                : "-"}
            </div>
          );
        },
      },
      {
        field: "actual_end_date",
        name: t("actual_end_date"),
        width: 120,

        render(data: any) {
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);
          return (
            <div className="w-full flex justify-center items-center">
              {project?.actualEndDate
                ? dateFormater(new Date(project?.actualEndDate))
                : "-"}
            </div>
          );
        },
      },
      {
        field: "Re_calculated_end_date",
        name: t("Re_calculated_end_date"),
        width: 120,
        render(data: any) {
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);
          return (
            <div className="w-full flex justify-center items-center">
              {project?.reCalculateEndDate
                ? dateFormater(new Date(project?.reCalculateEndDate))
                : "-"}
            </div>
          );
        },
      },
      {
        field: "spi",
        name: t("spi"),
        width: 120,
        render(data: any) { 
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => {
            return p.projectId == result
          });
          return (
            <div className="w-full flex justify-center items-center">
              {Number(project?.spi ?? 0).toFixed()}
            </div>
          );
        },
      },
      {
        field: "cpi",
        name: t("cpi"),
        width: 120,
        render(data: any) {
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);
          return (
            <div className="w-full flex justify-center items-center">
              {Number(project?.cpi ?? 0).toFixed()}
            </div>
          );
        },
      },
      {
        field: "estimated_budget",
        name: t("estimated_budget"),
        width: 120,
        render(data: any) {
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => {
            return p.projectId == result
          });
          return (
            <div className="w-full flex justify-center items-center">
              {project?.estimatedBudget
                ? project?.estimatedBudget == "undefined"
                  ? "-"
                  : project?.estimatedBudget
                : "-"}
            </div>
          );
        },
      },
      {
        field: "consumed_budget",
        name: t("consumed_budget"),
        width: 120,
        render(data: any) {
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => {
            return p.projectId == result
          });
          return (
            <div className="w-full flex justify-center items-center">
              {project?.consumedBudget
                ? project?.consumedBudget == "undefined"
                  ? "-"
                  : Number(project?.consumedBudget).toFixed()
                : "-"}
            </div>
          );
        },
      },
      {
        field: "Re-calculated_Budget",
        name: t("Re-calculated_Budget"),
        width: 120,
        render(data: any) {
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => {
            return p.projectId == result
          });
          return (
            <div className="w-full flex justify-center items-center">
              {project?.reCalculateBudget
                ? project?.reCalculateBudget == "undefined"
                  ? "-"
                  : Number(project?.reCalculateBudget).toFixed(0)
                : "-"}
            </div>
          );
        },
      },
    ];

    if (uniqueHiddenColumns.length > 0) {
      col.push({
        field: "add new column",
        name: t("add_column"),
        width: 160,
        headerRender() {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger>{t("add_column")} </DropdownMenuTrigger>
              <DropdownMenuContent>
                {uniqueHiddenColumns.map((key) => {
                  const field = col.find((t: any) => t.field == key)?.name;
                  return (
                    <DropdownMenuItem key={key} onSelect={() => AddColumn(key)}>
                      <div className="h-full w-full cursor-pointer">
                        {t(field ?? "")}
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        render() {
          return <></>;
        },
      });
    }
    if (!sortCol.length) {
      setSortCol(
        col.map((t) => {
          return { field: t.field, width: t.width };
        })
      );
    }
    return col;
  }, [t, projects, hiddenColumns, sortCol]);
  
  useEffect(() => {
    // columns that were unhidden
    const unhidden = allCols.filter(
      (col) => !uniqueHiddenColumns.includes(col.field)
    );

    // find specific column
    const find = unhidden.find((t) => t.field === "add new column");

    if (find) {
      setSortCol((prev: any) => {
        if (!prev.some((t: any) => t.field === find.field)) {
          return [...prev, { field: find.field, width: find.width }];
        }
        return prev;
      });
    }
  }, [uniqueHiddenColumns, allCols]);

  const visibleCols = useMemo(() => {
    const filteredCols = allCols.filter(
      (c: any) => !hiddenColumns.includes(c.field)
    );
    if (sortCol && sortCol.length > 0) {
      const sortedCol = sortCol
        .map((element: any) =>
          filteredCols.find((t) => t.field == element.field)
            ? {
                ...filteredCols.find((t) => t.field == element.field)!,
                width: element.width,
              }
            : null
        )
        .filter(Boolean); // remove nulls in case sortCol has old fields
      return sortedCol.length > 0 ? sortedCol : filteredCols;
    }
    return filteredCols;
  }, [allCols, hiddenColumns, sortCol]);

  const AddColumn = (field: string) => {
    if (hiddenColumns.includes(field)) {
      const updated = hiddenColumns.filter((item) => item !== field);
      localStorage.setItem("projects_hidden_columns", JSON.stringify(updated));

      const findcol = allCols.find((item) => item.field === field);
      if (findcol) {
        setSortCol((prev: any) => {
          // Always work from the current allCols order
          const baseCols = prev.length
            ? prev
            : allCols.map((c) => ({ field: c.field, width: c.width }));

          // Avoid duplicates
          if (baseCols.some((c: any) => c.field === findcol.field))
            return baseCols;

          // Insert before the "add new column" field if it exists
          const insertIndex = Math.max(baseCols.length - 1, 0);
          const newCol = [...baseCols];
          newCol.splice(insertIndex, 0, {
            field: findcol.field,
            width: findcol.width,
          });
          return newCol;
        });
      }

      setHiddenColumns(updated);
    }
  };

  const hideColumn = (field: any[]) => {

    setSortCol(
      field.map((t) => {
        return { field: t.field, width: t.width };
      })
    );
    const stored = localStorage.getItem("projects_hidden_columns") ?? "";
    setHiddenColumns(JSON.parse(stored));
  };
  useEffect(() => {
    updateHeight();
  }, [hiddenColumns, allCols, visibleCols, sortCol]);
  return (
    <div className={`w-full ${isOpenWorkLoad?"h-[75%]":"h-[80%]"}`} ref={Divref}>
      {view && (
        <Gantt
          resourcesTask={convertedTasks.map(t=>{
            const findProject = projects.find(p=>p.projectId == t.id && t.type == "task")
            if (findProject) {
              return {...t,subtask:findProject.tasks}
            }
            return t
          })}
          resourceMember={OrganisationMemeber}
          canCreateDependency={false}
          key={GanttHeight.current}
          startEndIndicater={false}
          ganttHeight={GanttHeight.current}
          onExpanderClick={(e) => expandToggle(e.id)}
          newTask={undefined}
          tasks={convertedTasks}
          taskSorted={setConvertedTasks as any}
          columns={visibleCols}
          projectStartDate={ganttDate.start}
          projectEndDate={findLatestOverallEndDate(projects) ?? ganttDate.end}
          preStepsCount={2}
          showWorkload={isOpenWorkLoad}
          holidays={
            user?.userOrganisation[0].organisation.orgHolidays?.map(
              (h) => new Date(h.holidayStartDate)
            ) ?? []
          }
          setColumns={hideColumn}
          viewMode={view}
          nonWorkingDays={nonWorkingDays()}
          showContextManu={true}
          onContextManuEvent={function (): void {}}
          onScroll={function (): void {}}
          onDepandanyClick={function (): void {}}
          onColorChange={function (): void {}}
          onDepandancyDraging={function (): void {}}
          onDepandancyDragEnd={function (): void {}}
          onDateChange={
            selectedDateType !== "actual"
              ? (e) => projectUpdate(e as GanttTaskExtendedType)
              : undefined
          }
          onDoubleClick={(e) => {
            if (e.type == "task") {
              const projectId = e.id.endsWith("-est")
                ? e.id.split("-est")[0]
                : e.id.endsWith("-act")
                ? e.id.split("-act")[0]
                : e.id.endsWith("-recalc")
                ? e.id.split("-recalc")[0]
                : e.id;
              const project = projects.find((p) => p.projectId === projectId);
              if (!project || !project?.isActive) {
                return;
              }
              const navbar = document.getElementById("navbar");
              const sidebar = document.getElementById("Sidebar");
              if (navbar) navbar.classList.remove("!hidden");
              if (sidebar) sidebar.classList.remove("!hidden");
              if (
                user?.subscription?.planType == PlanTypeEnumSchema.FREE ||
                !user?.subscription
              ) {
                navigate("/tasks/" + projectId);
              } else {
                navigate("/projectDashboard/" + projectId);
              }
            }
          }}
          onProgressChange={undefined}
          TooltipContent={StandardTooltipContent}
          ContextMenuContent={contextmanu}
        />
      )}
      <Dialog
        isOpen={Boolean(remove)}
        onClose={() => setRemove(undefined)}
        modalClass="rounded-lg"
      >
        <div className="flex flex-col gap-2 p-6 ">
          <img src={TrashCan} className="w-12 m-auto" />{" "}
          {t("common_delete_message")}
          <div className="flex gap-2 ml-auto">
            <Button
              variant={"outline"}
              isLoading={removeProjectMutation.isPending}
              disabled={removeProjectMutation.isPending}
              onClick={() => setRemove(undefined)}
            >
              {t("cancel")}
            </Button>
            <Button
              variant={"primary"}
              className="text-white"
              onClick={removeProject}
            >
              {t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>
      {projectId && (
        <TaskSubTaskForm
          taskId={undefined}
          projectId={projectId}
          close={() => setProjectId("")}
        />
      )}
    </div>
  );
}

export default GanttView;
